import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule, provideNativeDateAdapter } from '@angular/material/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MeetingNotesActions } from '@store/actions/meeting-notes/meeting-notes.actions';
import { selectMeetingNotesUploading } from '@store/selectors/meeting-notes/meeting-notes.selectors';

@Component({
  selector: 'app-meeting-notes-upload-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    MatIconModule,
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './meeting-notes-upload-modal.component.html',
  styleUrl: './meeting-notes-upload-modal.component.css',
})
export class MeetingNotesUploadModalComponent implements OnInit, OnDestroy {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly dialogRef = inject(MatDialogRef<MeetingNotesUploadModalComponent>);
  private readonly fb = inject(FormBuilder);
  private readonly destroy$ = new Subject<void>();

  form: FormGroup = this.fb.group({
    meetingDescription: ['', Validators.required],
    meetingDate: [null, Validators.required],
    sendNotification: [false],
  });

  readonly maxDate = new Date();

  selectedFile: File | null = null;
  fileTypeError: string | null = null;
  isDragging = false;
  uploading$ = this.store.select(selectMeetingNotesUploading);

  private readonly ALLOWED_TYPES = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
  ];
  private readonly ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx'];

  ngOnInit(): void {
    this.actions$.pipe(
      ofType(MeetingNotesActions.uploadMeetingNoteSuccess),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.dialogRef.close();
    });
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    if (event.dataTransfer && event.dataTransfer.files.length > 0) {
      this.validateAndSelectFile(event.dataTransfer.files[0]);
    }
  }

  onFileSelected(event: Event): void {
    const element = event.target as HTMLInputElement;
    if (element.files && element.files.length > 0) {
      this.validateAndSelectFile(element.files[0]);
    }
  }

  triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }

  private validateAndSelectFile(file: File): void {
    this.fileTypeError = null;
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!this.ALLOWED_TYPES.includes(file.type) && !this.ALLOWED_EXTENSIONS.includes(ext)) {
      this.fileTypeError = 'Only PDF and Word documents (.doc, .docx) are supported.';
      return;
    }
    this.selectedFile = file;
  }

  removeFile(): void {
    this.selectedFile = null;
    this.fileTypeError = null;
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  get canSave(): boolean {
    return this.form.valid && this.selectedFile !== null;
  }

  onSave(): void {
    if (!this.canSave || !this.selectedFile) return;

    const { meetingDescription, meetingDate, sendNotification } = this.form.value;
    const isoDate = meetingDate instanceof Date
      ? `${meetingDate.getFullYear()}-${String(meetingDate.getMonth() + 1).padStart(2, '0')}-${String(meetingDate.getDate()).padStart(2, '0')}`
      : meetingDate;

    this.store.dispatch(MeetingNotesActions.uploadMeetingNote({
      file: this.selectedFile,
      meetingDescription,
      meetingDate: isoDate,
      sendNotification: !!sendNotification,
    }));
  }

  onClose(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
